import { Box, Dropdown, Menu, MenuButton, MenuItem } from "@mui/joy";
import { schemeTableau10, sort, stackOrderAppearance } from "d3";
import {
  ApiRoutesTarget,
  ApiRoutesTargetConfig,
  ApiRoutesTargetsResponse,
} from "griptonite-api-client";
import { sumBy } from "lodash";
import { useEffect, useMemo, useState } from "react";

import { useVenueCompetitionsByUuid } from "@/hooks/competitions";
import { useTargets } from "@/hooks/targets";

import { Route } from "../../core/types";
import { useGradesQuery } from "../../hooks/grades";
import {
  useVenueCircuitsByUuid,
  useVenueLinesByUuid,
  useVenueRouteSettersByUuid,
  useVenueRouteStatsByUuid,
  useVenueZonesByClimbType,
  useVenueZonesByUuid,
} from "../../hooks/routes";
import { useCurrentClimbType, useCurrentVenueQuery, useCurrentVenueUuid } from "../../hooks/venues";
import { MapValue } from "../../utils/types";
import { useRoutesUiContext } from "../RoutesProvider/RoutesProvider";
import { useRoutesTableStore } from "../RoutesTable/RoutesProvider2";
import AnalyseGraph from "./AnalyseGraph";
import {
  BreakdownId,
  DimensionId,
  dimensions,
  getBreakdownDimension,
  getBreakdownDimensionId,
} from "./dimensions";
import { MetricId, metricGetters, metrics } from "./metrics";
import { GraphData, GraphDataMap, VenueDataBag } from "./types";

/**
 * Helper to determine if the routes count meets the target using thresholds from `config`
 * @param actual - The actual count value
 * @param target - The target count to compare against
 * @param config - Configuration with tolerance and warning thresholds
 * @returns Status level: undefined (OK), "warning", or "danger"
 */
const getTargetStatus = (
  actual: number,
  target: number | undefined,
  config: ApiRoutesTargetConfig | undefined,
): undefined | "warning" | "danger" => {
  if (target === undefined || config === undefined) {
    return undefined;
  }
  const tolerance = config.tolerance || 0;
  const warningThreshold = config.warningThreshold || 0;
  const diff = Math.abs(actual - target);

  if (diff <= tolerance) {
    return undefined;
  } else if (diff <= warningThreshold) {
    return "warning";
  } else {
    return "danger";
  }
};

/**
 * Organizes targets into a nested map structure by grade, then by zone/ascent style
 * @param targets - Array of route targets to organize
 * @returns Object with nested maps and totals per grade
 */
const getTargetsMap = (
  targets: ApiRoutesTarget[] | undefined,
  targetMode?: "GLOBAL" | "BY_ZONE" | "BY_ASCENT_STYLE",
) => {
  targetMode = targetMode || "GLOBAL";
  const targetsMapByGrade = new Map<string, Map<string, ApiRoutesTarget>>();
  targets?.forEach((target) => {
    if (!target.gradeValue) {
      return;
    }
    if (!targetsMapByGrade.has(target.gradeValue)) {
      targetsMapByGrade.set(target.gradeValue, new Map());
    }
    const targetMapForGrade = targetsMapByGrade.get(target.gradeValue)!;
    if (targetMode === "BY_ZONE") {
      targetMapForGrade.set(target.zone || "", target);
    } else if (targetMode === "BY_ASCENT_STYLE") {
      targetMapForGrade.set(target.ascentStyle, target);
    } else {
      targetMapForGrade.set("global", target);
    }
  });
  // Calculate total target count per grade across all zones/styles
  const targetsMapTotals = new Map<string, number>();
  targetsMapByGrade.forEach((targetMap, grade) => {
    const total = sumBy(Array.from(targetMap.values()), (t) => t.targetCount);
    targetsMapTotals.set(grade, total);
  });
  return {
    targetsMapByGrade,
    targetMode,
    targetsMapTotals,
  };
};

const getTargetKey = (axisValue: string | null) => axisValue?.split(":")[2] || "";

/**
 * Process route data to generate a set of graph data ready to be visualised
 * @param routes - Array of routes to analyze
 * @param bag - Venue data bag containing circuits, zones, setters, etc.
 * @param axis - Primary dimension for the x-axis
 * @param breakdown - Optional dimension to segment data within each axis value
 * @param metric - Metric to calculate (count, attempts, etc.)
 * @param sortByMetric - Whether to sort by metric value instead of natural order
 * @param targetData - Optional target data for displaying route count targets and statuses
 * @param isFiltered - Indicates if the routes data is filtered
 * @returns Formatted graph data with axis labels, colors, and aggregated values
 */
export const getGraphData = ({
  routes,
  bag,
  axis,
  breakdown,
  metric,
  sortByMetric = false,
  targetData,
  isFiltered = false,
}: {
  routes: Route[];
  bag: VenueDataBag;
  axis: DimensionId;
  breakdown: BreakdownId | null;
  metric: MetricId;
  sortByMetric?: boolean;
  targetData?: ApiRoutesTargetsResponse;
  isFiltered?: boolean;
}) => {
  const axisDimension = dimensions[axis];
  const breakdownDimension = getBreakdownDimension(breakdown);

  let axisData: GraphDataMap = new Map();
  let breakdownKeys = new Set<string | null>();

  const { targetsMapByGrade, targetsMapTotals } = getTargetsMap(
    targetData?.targets,
    targetData?.config.mode,
  );

  // It only makes sense to compute and show targets if the x-axis is grade
  const shouldAddTargets =
    targetData &&
    !!targetData.targets.length &&
    axis === "grade" &&
    metric === "count" &&
    !isFiltered;

  // Computing breakdown targets requires matching config mode
  const shouldAddBreakdownTargets =
    targetData &&
    !!targetData.targets.length &&
    axis === "grade" &&
    (((breakdown === "zone" || breakdown === "sportZone") &&
      targetData.config.mode === "BY_ZONE") ||
      (breakdown === "ascentStyle" && targetData.config.mode === "BY_ASCENT_STYLE")) &&
    !isFiltered;

  // Iterate through routes to aggregate data
  routes.forEach((route) => {
    const axisValue = axisDimension.getter(route, bag);
    const breakdownValue = breakdownDimension ? breakdownDimension.getter(route, bag) : null;

    breakdownKeys.add(breakdownValue);

    const axisDatum = axisData.get(axisValue) || {
      id: axisValue,
      name: axisDimension.getName(axisValue, bag),
      sortKey:
        ("sortKey" in axisDimension ? axisDimension.sortKey?.(route) : undefined) ?? axisValue,
      values: new Map<
        string | null,
        {
          id: string | null;
          name: string;
          value: number;
          target?: number;
          targetStatus?: "warning" | "danger";
        }
      >(),
      uuids: new Set<string>(),
      total: 0,

      targetTotal: shouldAddTargets
        ? targetsMapTotals.get(getTargetKey(axisValue)) || 0
        : undefined,
      targetStatus: shouldAddTargets
        ? getTargetStatus(0, targetsMapTotals.get(getTargetKey(axisValue)) || 0, targetData?.config)
        : undefined,
    };

    axisDatum.uuids.add(route.uuid);

    // Handle attempts counts metric (splits into multiple breakdown keys)
    if (metric === "attemptsCounts") {
      const metricValues = metricGetters["attemptsCounts"](route, bag);
      Object.entries(metricValues).forEach(([key, value]) => {
        const breakdownDatum = axisDatum.values.get(key) || {
          id: key,
          name: metrics["attemptsCounts"].keyLabels[
            key as keyof (typeof metrics)["attemptsCounts"]["keyLabels"]
          ],
          value: 0,
        };
        breakdownKeys.add(key);
        breakdownDatum.value += value;
        axisDatum.values.set(key, breakdownDatum);
      });
      axisDatum.total = metricValues.attemptsCount;
      axisData.set(axisValue, axisDatum);
    } else {
      // Handle standard metrics (count, etc.)
      const metricGetter = metricGetters[metric];
      const metricValue = metricGetter(route);
      if (breakdownDimension) {
        // Aggregate metric value into breakdown category
        const breakdownDatum = axisDatum.values.get(breakdownValue) || {
          id: breakdownValue,
          name: breakdownDimension.getName(breakdownValue, bag),
          value: 0,
          target:
            shouldAddBreakdownTargets && breakdownValue
              ? targetsMapByGrade.get(getTargetKey(axisValue))?.get(breakdownValue)?.targetCount
              : undefined,
        };
        breakdownDatum.value += metricValue;
        if (shouldAddBreakdownTargets && breakdownValue) {
          const target = breakdownDatum.target;
          breakdownDatum.targetStatus = getTargetStatus(
            breakdownDatum.value,
            target,
            targetData?.config,
          );
        }

        axisDatum.values.set(breakdownValue, breakdownDatum);
      }
      axisDatum.total += metricValue;
      axisDatum.targetStatus = shouldAddTargets
        ? getTargetStatus(axisDatum.total, axisDatum.targetTotal || 0, targetData?.config)
        : undefined;
      axisData.set(axisValue, axisDatum);
    }
  });

  /**
   * Fills missing breakdown keys with zero values and adds target statuses
   * @param axisData - The axis data map to modify
   * @param breakdownKeys - Set of all breakdown keys
   * @param targetsMapByGrade - Map of targets by grade
   * @param targetData - Target configuration data
   * @param breakdownDimension - The breakdown dimension definition
   * @param bag - Venue data bag for getting names
   */
  const fillMissingBreakdownKeysAndAddTargets = (
    axisData: GraphDataMap,
    breakdownKeys: Set<string | null>,
    targetsMapByGrade: Map<string, Map<string, ApiRoutesTarget>>,
    targetData: ApiRoutesTargetsResponse,
    breakdownDimension: (typeof dimensions)[DimensionId] | null,
    bag: VenueDataBag,
  ) => {
    axisData.forEach((axisDatum, axisValue) => {
      const gradeValue = axisValue?.split(":")[2];
      if (!gradeValue) return;

      const targetsForGrade = targetsMapByGrade.get(gradeValue);
      if (!targetsForGrade) return;

      // Add missing breakdown keys with zero values and targets
      breakdownKeys.forEach((breakdownValue) => {
        if (!axisDatum.values.has(breakdownValue)) {
          const target = targetsForGrade.get(breakdownValue || "")?.targetCount;
          axisDatum.values.set(breakdownValue, {
            id: breakdownValue,
            name: breakdownDimension ? breakdownDimension.getName(breakdownValue, bag) : "",
            value: 0,
            target,
            targetStatus: getTargetStatus(0, target, targetData?.config),
          });
        }
      });

      // // Ensure all targets are represented as breakdown keys
      // targetsForGrade.forEach((target, targetKey) => {
      //   if (!axisDatum.values.has(targetKey)) {
      //     breakdownKeys.add(targetKey);
      //     axisDatum.values.set(targetKey, {
      //       id: targetKey,
      //       name: breakdownDimension
      //         ? breakdownDimension.getName(targetKey, bag)
      //         : "",
      //       value: 0,
      //       target: target.targetCount,
      //       targetStatus: getTargetStatus(
      //         0,
      //         target.targetCount,
      //         targetData?.config
      //       ),
      //     });
      //   }
      // });
    });
  };

  if (shouldAddBreakdownTargets) {
    // If we're showing breakdown targets, then we need to modify the axis data to include missing breakdown keys (filled with 0) along with adding the targets
    fillMissingBreakdownKeysAndAddTargets(
      axisData,
      breakdownKeys,
      targetsMapByGrade,
      targetData,
      breakdownDimension,
      bag,
    );
  }

  // Apply dimension-specific postprocessing (e.g., filling missing data)
  if (breakdown && breakdownDimension && "postprocess" in breakdownDimension) {
    const { axisData: newAxisData, breakdownKeys: newBreakdownKeys } =
      breakdownDimension.postprocess(breakdown, axisData, breakdownKeys, bag);
    axisData = newAxisData;
    breakdownKeys = newBreakdownKeys;
  }

  // Sort axis data by metric value or natural ordering
  let sortedAxisData;
  if (sortByMetric) {
    sortedAxisData = sort(axisData.values(), (x) => -x.total);
  } else {
    sortedAxisData = Array.from(axisData.values());
    const comparator =
      "sortComparator" in axisDimension
        ? (a: MapValue<GraphDataMap>, b: MapValue<GraphDataMap>) =>
            axisDimension.sortComparator(a.sortKey, b.sortKey)
        : undefined;
    if (comparator) {
      sortedAxisData.sort(comparator);
    } else {
      sortedAxisData = sort(sortedAxisData, (x) => x.sortKey);
    }
  }

  // Calculate max value for chart scaling
  let maxTotal;
  if (metric === "attemptsCounts") {
    maxTotal = Math.max(
      ...Array.from(axisData.values(), (d) =>
        Math.max(...Array.from(d.values.values(), (dd) => dd.value)),
      ),
    );
  } else {
    maxTotal = Math.max(
      ...Array.from(axisData.values(), (d) => Math.max(d.total, d.targetTotal || 0)),
    );
  }

  // Transform to flat array format for d3 stacking helpers
  const dataArray = (() => {
    if (breakdown) {
      return sortedAxisData
        .map((d) =>
          Array.from(d.values.values(), (dd) => ({
            axis: d.id,
            breakdown: dd.id,
            value: dd.value,
            // ...(axis === 'grade' && ((targetMode === 'zone' && breakdown === 'zone' || breakdown === 'sportZone') || (targetMode === 'ascentStyle' && breakdown === 'ascentStyle'))) ? {
            //   target: targetsMapByGrade.get(d.id)?.get(dd.id)?.targetValue,
            // }
          })),
        )
        .flat();
    } else {
      return sortedAxisData.map((d) => ({
        axis: d.id,
        breakdown: null,
        value: d.total,
      }));
    }
  })();

  const breakdownColors = Object.fromEntries(
    Array.from(breakdownKeys, (key, i) => [
      key || "",
      metric === "attemptsCounts"
        ? metrics.attemptsCounts.colorMap[
            key as keyof (typeof metrics)["attemptsCounts"]["colorMap"]
          ]
        : breakdownDimension && "getColor" in breakdownDimension
          ? breakdownDimension.getColor(key, bag)
          : key === null
            ? "#F1F1F4"
            : schemeTableau10[i % 10], //stringToColor(key || ""),
    ]),
  );

  return {
    data: dataArray,
    axisKeys: sortedAxisData.map((x) => x.id || ""),
    axisLabels: new Map(sortedAxisData.map((x) => [x.id || "", x.name || ""])),
    breakdownKeys: Array.from(breakdownKeys, (x) => x || ""),
    breakdownColors,
    breakdownStackOrder: axisDimension.breakdownStackOrder || stackOrderAppearance,
    breakdownOverlap: metric === "attemptsCounts",
    maxTotal,
    axisDataMap: axisData,
    showTargets: shouldAddTargets,
  };
};

const emptyArray = [] as never[];
const emptyObject = {} as Record<string, never>;

/**
 * Fetches and transforms route data for visualization based on selected dimensions
 * @param axis - The primary dimension for grouping data
 * @param breakdown - Optional secondary dimension for segmenting data
 * @param metric - The metric to calculate and display
 * @param sortByMetric - Whether to sort results by metric value
 * @returns Memoized graph data ready for visualization
 */
const useData = ({
  axis,
  breakdown,
  metric,
  sortByMetric,
}: {
  axis: DimensionId;
  breakdown: BreakdownId | null;
  metric: MetricId;
  sortByMetric: boolean | undefined;
}): GraphData => {
  // Make sure we load in grades if they're not present already
  useGradesQuery();

  const { data: venue } = useCurrentVenueQuery();
  const currentClimbType = useCurrentClimbType();

  const { filteredRoutes: routes, routes: allRoutes } = useRoutesUiContext();

  const { isFetched: isGradesLoaded } = useGradesQuery();

  const { data: targetData } = useTargets(venue?.uuid, currentClimbType);

  const circuits = useVenueCircuitsByUuid(venue?.uuid);
  const { data: routeStats } = useVenueRouteStatsByUuid(venue?.uuid);

  const setters = useVenueRouteSettersByUuid(venue?.uuid);
  const zones = useVenueZonesByUuid(venue?.uuid);
  const lines = useVenueLinesByUuid(venue?.uuid);

  const competitions = useVenueCompetitionsByUuid(venue?.uuid);

  // Memoize graph data to avoid recalculation on unrelated changes
  return useMemo(
    () =>
      getGraphData({
        routes: isGradesLoaded && routes ? routes : emptyArray,
        isFiltered: routes.length !== allRoutes?.length,
        bag: {
          circuits: circuits || emptyObject,
          venue,
          routeStats: routeStats || emptyObject,
          zones: zones || emptyObject,
          lines: lines || emptyObject,
          setters: setters || emptyObject,
          competitions: competitions || emptyObject,
        },
        axis,
        breakdown,
        metric,
        targetData,
        sortByMetric,
      }),
    [
      isGradesLoaded,
      routes,
      circuits,
      venue,
      competitions,
      routeStats,
      zones,
      lines,
      setters,
      axis,
      breakdown,
      metric,
      sortByMetric,
      targetData,
      allRoutes?.length,
    ],
  );
};

type Preset = {
  id: string;
  metric: MetricId;
  label: string;
  sortByMetric?: boolean;
} & (
  | { breakdownDefault: DimensionId; breakdownChoices?: DimensionId[] }
  | { breakdown: BreakdownId | null }
) &
  ({ axis: DimensionId } | { defaultAxis: DimensionId; axisChoices: DimensionId[] });

/**
 * Generates available analysis presets based on venue configuration
 * @returns Array of preset configurations tailored to current venue features
 */
export const usePresets = () => {
  const currentVenueUuid = useCurrentVenueUuid();
  const climbType = useCurrentClimbType();
  const { data: zones } = useVenueZonesByClimbType(currentVenueUuid, climbType);
  const { data: sportZones } = useVenueZonesByClimbType(currentVenueUuid, "SPORT");

  const hasZones = !!zones?.length && climbType === "BOULDER";
  const hasSportZones = !!sportZones?.length && climbType === "SPORT";

  const presets = useMemo(() => {
    return [
      {
        id: "gradeBalance",
        axis: "grade",
        breakdownDefault: hasZones
          ? climbType === "BOULDER"
            ? "zone"
            : "sportZone"
          : climbType === "SPORT"
            ? "line"
            : "circuit",
        breakdownChoices: [
          ...(hasZones ? (["zone"] as const) : []),
          ...(hasSportZones ? (["sportZone"] as const) : []),
          climbType === "SPORT" ? "line" : "circuit",
          ...(climbType === "SPORT" ? ["ascentStyle" as const] : []),
          "setters",
          "starRating",
        ],
        metric: "count",
        label: "Grade Spread",
      },
      {
        id: "traitBalance",
        defaultAxis: "grade",
        axisChoices: [
          "grade",
          ...(hasZones ? (["zone"] as const) : ([] as const)),
          ...(hasSportZones ? (["sportZone"] as const) : []),
          climbType === "SPORT" ? "line" : "circuit",
          ...(climbType === "SPORT" ? ["ascentStyle" as const] : []),
        ],
        breakdownDefault: "styles",
        breakdownChoices: ["styles", "skills", "holds"],
        metric: "count",
        label: "Trait Balance",
      },
      {
        id: "routePopularity",
        defaultAxis: "route",
        axisChoices: [
          "route",
          "grade",
          ...(hasZones ? (["zone"] as const) : ([] as const)),
          ...(hasSportZones ? (["sportZone"] as const) : []),
          climbType === "SPORT" ? "line" : "circuit",
          ...(climbType === "SPORT" ? ["ascentStyle" as const] : []),
          "setters",
        ],
        breakdown: "attemptsCounts",
        metric: "attemptsCounts",
        label: "Route Popularity",
        sortByMetric: true,
      },
    ] as const satisfies Preset[];
  }, [hasZones, climbType, hasSportZones]);

  return presets;
};

/**
 * Main analysis component for visualizing route data across different dimensions
 * Provides preset-based analysis views with configurable axis and breakdown options
 */
export default function Analyse() {
  const setHighlightedRouteUuids = useRoutesTableStore((state) => state.setHighlightedRouteUuids);
  // Track current visualization configuration
  const [axis, setAxis] = useState<DimensionId>("grade");
  const [metric, setMetric] = useState<MetricId>("count");
  const [breakdown, setBreakdown] = useState<BreakdownId | null>("circuit");
  const [presetId, setPresetId] = useState("gradeBalance");
  const [sortByMetric, setSortByMetric] = useState<boolean | undefined>(undefined);

  const presetChoices = usePresets();
  const selectedPreset = useMemo(() => {
    return presetChoices.find((x) => x.id === presetId);
  }, [presetId, presetChoices]);

  // Update visualization settings when preset changes
  useEffect(() => {
    const preset = presetChoices.find((x) => x.id === presetId);
    if (preset) {
      if ("axis" in preset) {
        setAxis(preset.axis);
      } else {
        setAxis(preset.defaultAxis);
      }
      setMetric(preset.metric);
      if ("breakdown" in preset) {
        setBreakdown(preset.breakdown);
      } else {
        setBreakdown(preset.breakdownDefault);
      }
      setSortByMetric("sortByMetric" in preset ? preset.sortByMetric : undefined);
    }
  }, [selectedPreset, presetId, presetChoices]);

  const handleSelectAxis = (id: DimensionId) => {
    setAxis(id);
    if (selectedPreset?.id === "routePopularity") {
      setSortByMetric(id === "route");
    }
    if (breakdown === id) {
      setBreakdown(null);
    }
  };

  // Filter dimensions to only those allowed by current preset
  const availableBreakdowns = useMemo(() => {
    if (
      !selectedPreset ||
      ((!("breakdownChoices" in selectedPreset) || selectedPreset.breakdownChoices === undefined) &&
        (!("breakdown" in selectedPreset) || selectedPreset.breakdown === undefined))
    ) {
      return dimensions;
    }
    if ("breakdownChoices" in selectedPreset && selectedPreset.breakdownChoices) {
      return Object.fromEntries(selectedPreset.breakdownChoices.map((id) => [id, dimensions[id]]));
    } else if ("breakdown" in selectedPreset) {
      return Object.fromEntries(
        Object.entries(dimensions).filter(([id]) => {
          return id === selectedPreset.breakdown;
        }),
      );
    } else {
      throw new Error("Invalid preset");
    }
  }, [selectedPreset]);

  const selectedBreakdownId = getBreakdownDimensionId(breakdown);
  const selectedBreakdown = selectedBreakdownId
    ? availableBreakdowns[selectedBreakdownId]
    : undefined;

  // Filter axis options based on current preset
  const availableAxes = useMemo(() => {
    if (
      !selectedPreset ||
      ((!("axisChoices" in selectedPreset) || selectedPreset.axisChoices === undefined) &&
        (!("axis" in selectedPreset) || selectedPreset.axis === undefined))
    ) {
      return dimensions;
    }
    if ("axisChoices" in selectedPreset && selectedPreset.axisChoices) {
      return Object.fromEntries(selectedPreset.axisChoices.map((id) => [id, dimensions[id]]));
    } else if ("axis" in selectedPreset) {
      return Object.fromEntries(
        Object.entries(dimensions).filter(([id]) => {
          return id === selectedPreset.axis;
        }),
      );
    } else {
      throw new Error("Invalid preset");
    }
  }, [selectedPreset]);

  const data = useData({ axis, breakdown, metric, sortByMetric });

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", gap: 1, margin: 0.5 }}>
        {/* <Dropdown>
          <MenuButton size="sm">Axis: {dimensions[axis].label}</MenuButton>
          <Menu>
            {Object.entries(dimensions).map(([id, { label }]) => (
              <MenuItem onClick={() => handleSelectAxis(id as MetricId)}>
                {label}
              </MenuItem>
            ))}
          </Menu>
        </Dropdown>
        <Dropdown>
          <MenuButton size="sm">
            Breakdown: {breakdown ? dimensions[breakdown].label : "None"}
          </MenuButton>
          <Menu>
            <MenuItem onClick={() => setBreakdown(null)}>None</MenuItem>
            {Object.entries(dimensions).map(([id, { label }]) =>
              id !== axis ? (
                <MenuItem onClick={() => setBreakdown(id as MetricId)}>
                  {label}
                </MenuItem>
              ) : null
            )}
          </Menu>
        </Dropdown> */}
        <Dropdown>
          <MenuButton size="sm">Analysis type: {selectedPreset?.label}</MenuButton>
          <Menu>
            {presetChoices.map((preset) => (
              <MenuItem key={preset.id} onClick={() => setPresetId(preset.id)}>
                {preset.label}
              </MenuItem>
            ))}
          </Menu>
        </Dropdown>
        <Box sx={{ flex: 1 }}></Box>
        {Object.keys(availableAxes).length > 1 ? (
          <Dropdown>
            <MenuButton size="sm">
              {availableAxes[axis] ? `Axis: ${availableAxes[axis].label}` : "Axis"}
            </MenuButton>
            <Menu>
              {Object.entries(availableAxes).map(([id, { label }]) => (
                <MenuItem key={id} onClick={() => handleSelectAxis(id as DimensionId)}>
                  {label}
                </MenuItem>
              ))}
            </Menu>
          </Dropdown>
        ) : null}
        {Object.keys(availableBreakdowns).length > 1 ? (
          <Dropdown>
            <MenuButton size="sm">
              {selectedBreakdown ? `Breakdown: ${selectedBreakdown.label}` : "Breakdown"}
            </MenuButton>
            <Menu>
              {Object.entries(availableBreakdowns).map(([id, { label }]) =>
                id !== axis ? (
                  <MenuItem
                    key={id}
                    onClick={() => setBreakdown(id as DimensionId)}
                    selected={breakdown === id}
                  >
                    {label}
                  </MenuItem>
                ) : null,
              )}
            </Menu>
          </Dropdown>
        ) : null}
      </Box>
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <AnalyseGraph
          data={data}
          axis={axis}
          breakdown={breakdown}
          metric={metric}
          onColumnClick={(routeUuids) => setHighlightedRouteUuids(routeUuids)}
        />
      </Box>
    </Box>
  );
}
